//codex-- Reusable controlled picker: options contain value/label and optional searchText; onChange receives the selected value.
import React, { useEffect, useId, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

//codex-- Keep search and keyboard focus inside the panel without submitting the surrounding form.
function DropdownOptions({ id, label, options, value, onChange, close, searchPlaceholder, emptyMessage, required }) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.findIndex((option) => String(option.value) === String(value))));
  const listRef = useRef(null);
  const filteredOptions = options.filter((option) => `${option.label} ${option.searchText || ''}`.toLowerCase().includes(query.trim().toLowerCase()));
  const activeOption = filteredOptions[activeIndex];

  //codex-- Scroll keyboard-highlighted rows into view while leaving DOM focus in the searchable combobox.
  useEffect(() => {
    listRef.current?.children[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, query]);

  const choose = (option) => {
    onChange(option.value);
    close();
  };
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (!filteredOptions.length) return;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((index) => (index + direction + filteredOptions.length) % filteredOptions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (activeOption) choose(activeOption);
    }
  };

  return (
    <>
      {/* //codex-- The panel has its own labeled search field, separate from the selected-value trigger. */}
      <div className="relative m-2 shrink-0">
        <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          role="combobox"
          aria-label={`Search ${label}`}
          aria-expanded="true"
          aria-controls={`${id}-options`}
          aria-autocomplete="list"
          aria-required={required}
          aria-activedescendant={activeOption ? `${id}-option-${activeIndex}` : undefined}
          autoComplete="off"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          className="min-h-[44px] w-full rounded-xl border border-blue-400 bg-white py-2 pl-10 pr-3 text-base text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>
      {/* //codex-- Scroll rows without adding a native Tab stop; the search input owns keyboard navigation. */}
      <ul ref={listRef} id={`${id}-options`} role="listbox" tabIndex={-1} aria-labelledby={`${id}-label`} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {filteredOptions.map((option, index) => {
          const selected = String(option.value) === String(value);
          return (
            <li
              key={option.value}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={selected}
              onMouseMove={() => setActiveIndex(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
              className={`flex min-h-[44px] cursor-pointer items-center justify-between gap-2 break-words rounded-lg px-3 py-2 text-base ${selected ? 'bg-blue-500 text-white' : activeIndex === index ? 'bg-blue-50 text-blue-900' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <span className="min-w-0">{option.label}</span>
              {selected && <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0" />}
            </li>
          );
        })}
      </ul>
      {!filteredOptions.length && <p role="status" className="px-5 pb-4 text-sm text-slate-500">{emptyMessage}</p>}
    </>
  );
}

//codex-- Headless UI handles outside-click/Escape dismissal; anchoring portals the panel past modal overflow clipping.
export default function SearchableDropdown({ id, label, options = [], value = '', onChange, placeholder = 'Select an option', searchPlaceholder = 'Search', emptyMessage = 'No options found', disabled = false, required = false }) {
  const generatedId = useId();
  const fieldId = id || `searchable-dropdown-${generatedId}`;
  const selectedOption = options.find((option) => String(option.value) === String(value));

  return (
    <Popover className="min-w-0 w-full">
      {({ open, close }) => (
        <>
          {/* //codex-- Match the reference's rounded selector, blue focus border, and readable disabled state. */}
          <label id={`${fieldId}-label`} htmlFor={fieldId} className="mb-1 block text-sm font-semibold text-slate-600">
            {label}{required && <span aria-hidden="true" className="ml-0.5 text-red-600">*</span>}
          </label>
          <PopoverButton
            id={fieldId}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-labelledby={`${fieldId}-label ${fieldId}-value`}
            onKeyDown={(event) => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); event.currentTarget.click(); } }}
            className={`flex min-h-[44px] w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 py-2 text-left text-base focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${open ? 'border-blue-500' : 'border-slate-300 hover:border-blue-400 focus:border-blue-500'}`}
          >
            <span id={`${fieldId}-value`} className={`min-w-0 truncate ${selectedOption ? 'text-slate-700' : 'text-slate-400'}`}>{selectedOption?.label || placeholder}</span>
            <ChevronDownIcon aria-hidden="true" className={`h-5 w-5 shrink-0 text-blue-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          </PopoverButton>
          {/* //codex-- Fit the trigger width and available viewport height while scrolling only the result rows. */}
          <PopoverPanel focus anchor={{ to: 'bottom start', gap: 8, padding: 12 }} className="z-50 flex w-[var(--button-width)] max-w-[calc(100vw-24px)] flex-col overflow-hidden whitespace-normal rounded-2xl border border-slate-200 bg-white shadow-xl [--anchor-max-height:24rem]">
            <DropdownOptions id={fieldId} label={label} options={options} value={value} onChange={onChange} close={close} searchPlaceholder={searchPlaceholder} emptyMessage={emptyMessage} required={required} />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
